import { QueryKeysEnum } from '@/enums';
import { AnalysisParams, TenantParams } from '@/interfaces';
import { CommonParams } from '@/pages/privatePages/common/types';
import { CustomerParams } from '@/pages/privatePages/customer/types';
import { DayDelayParams } from '@/pages/privatePages/dayDelay/types';
import { FmtBottleParams } from '@/pages/privatePages/fmtBottle/types';
import { FmtBottleLogParams } from '@/pages/privatePages/fmtBottleLog/types';
import { KitParams } from '@/pages/privatePages/kit/types';
import { SampleParams } from '@/pages/privatePages/kit/types/sample';
import { OrderParams } from '@/pages/privatePages/order/types';
import { OrderItemParams } from '@/pages/privatePages/orderitem/types';
import { ProductParams } from '@/pages/privatePages/product/types';
import { ProductGroupParams } from '@/pages/privatePages/productgroup/types';
import { ProductHistoryParams } from '@/pages/privatePages/producthistory/types';
import { PromoCodeParams } from '@/pages/privatePages/promoCode/types';
import { TriggerPdfGenerationKitParams } from '@/pages/privatePages/trigger-pdf-generation-kit/types';
import { UserParams } from '@/pages/privatePages/user/types';
import { PipelineHistoryPdfStatusParams } from '@/services/api-services/pipeline-history.service';
import {
  AnalysisSampleKitParam,
  CreateIncidentAlertLoggerParam,
  CycleParams,
  DonorParams,
  ExportExtractionBatchParams,
  ExtractionParams,
  ExtractionSequenceParams,
  FmtProductParams,
  ProjectParams,
  ProjectTenantsParams,
  RecipientParams,
  ReportParams,
  ScreeningTestParams,
  SequenceParams,
  SequencingBatchParams,
  SingleScreeningTestParams,
  SortOrder,
} from '@/types';
import { EmailAlertParams } from '@/types/email-alert';
import { Dayjs } from 'dayjs';

export type DefaultParams = {
  q?: string | null;
  page?: number;
  take?: number;
  startDate?: string | Date | Dayjs;
  endDate?: string | Date | Dayjs;
  deleted?: boolean;
  tab?: string;
  roles?: string[] | number[];
  status?: string;
  order?: SortOrder;
  orderField?: string;
};

export type QueryKeyItemType<Params = unknown> = {
  scope?: QueryKeysEnum;
  entity?: string;
  id?: string;
  params?: Params;
  subScope?: string;
};

export type QueryKeyTypes<Params = unknown> = [QueryKeyItemType<Params>];

const createBasicListKey = <ListParams = unknown>({
  scope,
}: {
  scope: QueryKeysEnum;
}) => {
  const queryKeyFactory = {
    /**
     * Used to invalidate all queries related to this scope
     */
    invalidateAllKey: [{ scope }] as QueryKeyTypes,
    /**
     * Used to invalidate all list queries
     */
    invalidateListsKey: () =>
      [
        { ...queryKeyFactory.invalidateAllKey[0], entity: 'lists' },
      ] as QueryKeyTypes,
    /**
     * List query key with params
     */
    list: (params?: ListParams) =>
      [
        { ...queryKeyFactory.invalidateListsKey()[0], params },
      ] as QueryKeyTypes<ListParams>,
    /**
     * Used to invalidate all detail queries
     */
    invalidateDetailsKey: () =>
      [
        { ...queryKeyFactory.invalidateAllKey[0], entity: 'details' },
      ] as QueryKeyTypes,
    /**
     * Detail query key
     */
    detail: <T extends Record<string, any>>(id: string, params?: T) =>
      [
        { ...queryKeyFactory.invalidateDetailsKey()[0], id, ...(params || {}) },
      ] as QueryKeyTypes,
  };

  return queryKeyFactory;
};

export const newQueryKeys = {
  profile: createBasicListKey({
    scope: QueryKeysEnum.PROFILE,
  }),
  projectTenants: createBasicListKey<ProjectTenantsParams>({
    scope: QueryKeysEnum.PROJECT_TENANTS,
  }),
  channel: createBasicListKey({ scope: QueryKeysEnum.CHANNEL }),
  thirdParty: createBasicListKey({ scope: QueryKeysEnum.THIRD_PARTY }),
  organization: createBasicListKey<DefaultParams & TenantParams>({
    scope: QueryKeysEnum.ORGANIZATION,
  }),
  kit: createBasicListKey<KitParams>({ scope: QueryKeysEnum.KIT }),
  kitAttribute: createBasicListKey<DefaultParams>({
    scope: QueryKeysEnum.KIT_ATTRIBUTE,
  }),
  product: createBasicListKey<ProductParams>({ scope: QueryKeysEnum.PRODUCT }),
  project: createBasicListKey<ProjectParams>({
    scope: QueryKeysEnum.PROJECT,
  }),
  order: createBasicListKey<OrderParams>({ scope: QueryKeysEnum.ORDER }),

  orderStatus: createBasicListKey<OrderParams>({
    scope: QueryKeysEnum.ORDER_STATUS,
  }),

  dayDelay: createBasicListKey<DayDelayParams>({
    scope: QueryKeysEnum.DAY_DELAY,
  }),
  user: createBasicListKey<UserParams>({ scope: QueryKeysEnum.USER }),
  adminSetting: createBasicListKey<UserParams>({
    scope: QueryKeysEnum.ADMIN_SETTING,
  }),
  sample: createBasicListKey<SampleParams>({ scope: QueryKeysEnum.SAMPLE }),
  sampleExtraction: createBasicListKey<DefaultParams>({
    scope: QueryKeysEnum.SAMPLE_DNA_EXTRACTION,
  }),
  batch: createBasicListKey<SequencingBatchParams>({
    scope: QueryKeysEnum.BATCH,
  }), // belong to Sequencing Batch
  extraction: createBasicListKey<ExtractionParams>({
    scope: QueryKeysEnum.EXTRACTION,
  }), // belong to Extraction Batch
  extractionExport: createBasicListKey<ExportExtractionBatchParams>({
    scope: QueryKeysEnum.EXTRACTION_EXPORT,
  }),
  sequencingType: createBasicListKey<DefaultParams>({
    scope: QueryKeysEnum.SEQUENCING_TYPE,
  }),
  sequencingPartner: createBasicListKey<DefaultParams>({
    scope: QueryKeysEnum.SEQUENCING_PARTNER,
  }),
  sequence: createBasicListKey<SequenceParams>({
    scope: QueryKeysEnum.SEQUENCE,
  }),
  orderItem: createBasicListKey<OrderItemParams>({
    scope: QueryKeysEnum.ORDER_ITEM,
  }),
  productHistory: createBasicListKey<ProductHistoryParams>({
    scope: QueryKeysEnum.PRODUCT_HISTORY,
  }),
  report: createBasicListKey<ReportParams>({
    scope: QueryKeysEnum.REPORT,
  }), // 16s report
  analysisReport: createBasicListKey<ReportParams>({
    scope: QueryKeysEnum.ANALYSIS_REPORT,
  }),
  shotgunReport: createBasicListKey<ReportParams>({
    scope: QueryKeysEnum.SHOTGUN_REPORT,
  }),
  productgroup: createBasicListKey<ProductGroupParams>({
    scope: QueryKeysEnum.PRODUCT_GROUP,
  }),
  analysis: createBasicListKey<AnalysisParams>({
    scope: QueryKeysEnum.ANALYSIS,
  }), // research analysis
  analysisCommercial: createBasicListKey<AnalysisParams>({
    scope: QueryKeysEnum.ANALYSIS_COMMERCIAL,
  }),
  analysisSample: createBasicListKey<DefaultParams>({
    scope: QueryKeysEnum.ANALYSIS_SAMPLE,
  }), // belong to Research Analysis
  analysisSampleKit: createBasicListKey<DefaultParams>({
    scope: QueryKeysEnum.ANALYSIS_SAMPLE_KIT,
  }), // belong to Commercial Analysis
  triggerPdfGenerationKit: createBasicListKey<TriggerPdfGenerationKitParams>({
    scope: QueryKeysEnum.TRIGGER_PDF_GENERATION_KIT,
  }),
  role: createBasicListKey<DefaultParams>({
    scope: QueryKeysEnum.ROLE,
  }),
  recipient: createBasicListKey<RecipientParams>({
    scope: QueryKeysEnum.FMT_RECIPIENT,
  }),
  donor: createBasicListKey<DonorParams>({
    scope: QueryKeysEnum.FMT_DONOR,
  }),
  cycle: createBasicListKey<CycleParams>({
    scope: QueryKeysEnum.FMT_CYCLE,
  }),
  screeningTest: createBasicListKey<
    ScreeningTestParams | SingleScreeningTestParams
  >({
    scope: QueryKeysEnum.FMT_SCREENING_TEST,
  }),
  fmtProduct: createBasicListKey<FmtProductParams>({
    scope: QueryKeysEnum.FMT_PRODUCT,
  }),
  fmtBottleLog: createBasicListKey<FmtBottleLogParams>({
    scope: QueryKeysEnum.FMT_BOTTLE_LOG,
  }),
  fmtBottle: createBasicListKey<FmtBottleParams>({
    scope: QueryKeysEnum.FMT_BOTTLE,
  }),
  pdfAssets: createBasicListKey<{ paths: string[] }>({
    scope: QueryKeysEnum.PDF_ASSETS,
  }),
  analysisStream: createBasicListKey<AnalysisSampleKitParam>({
    scope: QueryKeysEnum.ANALYSIS_STREAM,
  }),
  customer: createBasicListKey<CustomerParams>({
    scope: QueryKeysEnum.CUSTOMER,
  }),
  promoCode: createBasicListKey<PromoCodeParams>({
    scope: QueryKeysEnum.PROMO_CODE,
  }),
  extractionSequence: createBasicListKey<ExtractionSequenceParams>({
    scope: QueryKeysEnum.EXTRACTION_SEQUENCE,
  }),
  validateAllowDnaExtraction: createBasicListKey<DefaultParams>({
    scope: QueryKeysEnum.VALIDATE_ALLOW_DNA_EXTRACTION,
  }),
  common: createBasicListKey<CommonParams>({
    scope: QueryKeysEnum.COMMON,
  }),
  invoice: createBasicListKey<DefaultParams>({
    scope: QueryKeysEnum.INVOICE,
  }),
  pipelineHistory: createBasicListKey<PipelineHistoryPdfStatusParams>({
    scope: QueryKeysEnum.PIPELINE_HISTORY,
  }),
  alert: createBasicListKey<DefaultParams>({
    scope: QueryKeysEnum.ALERT,
  }),
  alertConfig: createBasicListKey<EmailAlertParams>({
    scope: QueryKeysEnum.ALERT_CONFIG,
  }),
  incidentAlertLogger: createBasicListKey<CreateIncidentAlertLoggerParam>({
    scope: QueryKeysEnum.INCIDENT_ALERT_LOGGER,
  }),
};
